// @flow
// @flow-runtime warn
import React, { type Node, useMemo, useCallback, useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import path from 'ramda/es/path';
import clone from 'ramda/es/clone';
import { useTranslation } from 'react-i18next';

import api from 'app/actions/api';
import action from 'app/common/lib/action';

import SubsidyOrdersContext, { defaultSubsidyOrders } from './SubsidyOrdersContext';
import { type SubsidyOrders } from './SubsidyOrdersTypes';


type Props = {
  children: Node,
  personId: number,
  schoolId?: number,
}

const CafeteriaSubsidyOrdersData = ({ children, personId, schoolId }: Props) => {
  const dispatch = useDispatch();
  const { t } = useTranslation('cafeteria');
  const [subsidyOrders, setSubsidyOrders] = useState(defaultSubsidyOrders);
  const [loadingSubsidyOrders, setLoadingSubsidyOrders] = useState(true);

  const subsidyOrdersRequest = useCallback(async () => {
    const requestPath = 'cafeteria/customer/subsidies/orders';
    const type = 'get';
    const rest = {
      params: {
        customer_id: personId,
        school_id: schoolId,
        for_camp: !!schoolId,
      },
    };
    try {
      setLoadingSubsidyOrders(true);
      const res = await api(requestPath, type, rest);
      const subsidiesResult: SubsidyOrders = path(['data', 'data'], res) || defaultSubsidyOrders;
      setSubsidyOrders(subsidiesResult);
      setLoadingSubsidyOrders(false);
    } catch (error) {
      const errorSave = {
        err: t('subsidy_orders_fetch_error', 'Не удалось получить справки по дотациям'),
        path: requestPath,
        rest,
        type,
        error: clone(error),
      };
      dispatch(action('REQUEST_FAILURE', errorSave));
    }
  }, [dispatch, personId, t, schoolId]);

  useEffect(() => {
    subsidyOrdersRequest();
  }, [subsidyOrdersRequest]);

  const contextValue = useMemo(() => ({
    content: {
      subsidyOrders,
    },
    loading: {
      subsidyOrders: loadingSubsidyOrders,
    },
    request: {
      subsidyOrders: subsidyOrdersRequest,
    },
  }), [
    subsidyOrders,
    loadingSubsidyOrders,
    subsidyOrdersRequest,
  ]);

  return (
    <SubsidyOrdersContext.Provider value={contextValue}>
      {children}
    </SubsidyOrdersContext.Provider>
  );
};

export default CafeteriaSubsidyOrdersData;
